import { useEffect, useMemo, useRef, useState } from "react";
import { toast } from "sonner";

import { carregarIngredientes, carregarLotes, salvarCompra } from "./api";
import { apagarRascunho, lerRascunho, salvarRascunho } from "./lib/rascunho";
import { paraApi } from "./lib/unidades";
import useFornecedoresSugeridos from "./useFornecedoresSugeridos";

const hoje = () => new Date().toLocaleDateString("sv-SE");

// Estado da tela Nova/Editar Compra: dados, cabeçalho, itens, rascunho e gravação.
export default function useNovaCompra(loteId) {
  const [carregando, setCarregando] = useState(true);
  const [lotes, setLotes] = useState([]);
  const [ingredientes, setIngredientes] = useState({ todos: [], compraveis: [] });
  const [fornecedor, setFornecedor] = useState("");
  const [data, setData] = useState(hoje());
  const [paga, setPaga] = useState(true);
  const [vencimento, setVencimento] = useState("");
  const [itens, setItens] = useState([]);
  const [salvando, setSalvando] = useState(false);
  const [rascunhoRecuperado, setRascunhoRecuperado] = useState(false);
  // Editar uma compra que não existe (apagada, link velho): a tela volta para
  // Compras em vez de abrir um formulário vazio que gravaria em cima de nada
  const [naoEncontrada, setNaoEncontrada] = useState(false);
  const pronto = useRef(false);

  const porId = useMemo(() => new Map(ingredientes.todos.map((i) => [i.id, i])), [ingredientes.todos]);

  useEffect(() => {
    let vivo = true;
    Promise.all([carregarLotes(), carregarIngredientes()])
      .then(([ls, ings]) => {
        if (!vivo) return;
        setLotes(ls);
        setIngredientes(ings);
        if (loteId) {
          const lote = ls.find((l) => l.batch_id === loteId);
          if (!lote) { toast.error("Compra não encontrada"); setNaoEncontrada(true); return; }
          setFornecedor(lote.supplier || "");
          setData(String(lote.purchase_date || "").slice(0, 10) || hoje());
          setPaga(lote.is_paid !== false);
          setVencimento(lote.due_date || "");
          setItens((lote.items || []).map((p, n) => ({
            id: `lote-${n}`, ingredient_id: p.ingredient_id, nome: p.ingredient_name, qtdEstoque: p.quantity, total: p.price,
          })));
        } else {
          const r = lerRascunho();
          if (r && (r.itens.length || r.fornecedor)) {
            setFornecedor(r.fornecedor || ""); setData(r.data || hoje()); setPaga(r.paga !== false);
            setVencimento(r.vencimento || ""); setItens(r.itens); setRascunhoRecuperado(true);
          }
        }
      })
      .catch(() => toast.error("Não foi possível carregar os dados da compra"))
      .finally(() => { if (vivo) { setCarregando(false); pronto.current = true; } });
    return () => { vivo = false; };
  }, [loteId]);

  useEffect(() => {
    if (!loteId && pronto.current) salvarRascunho({ fornecedor, data, paga, vencimento, itens });
  }, [loteId, fornecedor, data, paga, vencimento, itens]);

  const fornecedores = useFornecedoresSugeridos(lotes);

  const total = itens.reduce((s, i) => s + (Number(i.total) || 0), 0);
  const alterado = itens.length > 0 || fornecedor.trim() !== "";

  const descartar = () => { apagarRascunho(); setItens([]); setFornecedor(""); setRascunhoRecuperado(false); };

  const finalizar = async () => {
    if (!itens.length) { toast.error("Adicione pelo menos um item"); return false; }
    if (!fornecedor.trim()) { toast.error("Informe o fornecedor"); return false; }
    if (!paga && !vencimento) { toast.error("Informe o vencimento da compra a pagar"); return false; }
    setSalvando(true);
    try {
      await salvarCompra(loteId, {
        supplier: fornecedor.trim(),
        // Meio-dia UTC mantém o dia escolhido em qualquer fuso do Brasil.
        purchase_date: `${data}T12:00:00.000Z`,
        items: itens.map((i) => ({ ingredient_id: i.ingredient_id, quantity: paraApi(porId.get(i.ingredient_id), i.qtdEstoque),
          price: Math.round(Number(i.total) * 100) / 100 })),
        is_paid: paga,
        due_date: paga ? null : vencimento,
      });
      if (!loteId) apagarRascunho();
      toast.success(loteId ? "Compra atualizada" : "Compra lançada");
      return true;
    } catch {
      toast.error(loteId ? "Não foi possível atualizar a compra" : "Não foi possível lançar a compra");
      return false;
    } finally {
      setSalvando(false);
    }
  };

  return {
    loteId, carregando, naoEncontrada, lotes, ingredientes: ingredientes.compraveis, porId, fornecedores, total, alterado, salvando, rascunhoRecuperado,
    fornecedor, setFornecedor, data, setData, paga, setPaga, vencimento, setVencimento,
    itens,
    adicionar: (item) => setItens((l) => [...l, { ...item, id: `${Date.now()}-${Math.random().toString(36).slice(2, 6)}` }]),
    remover: (id) => setItens((l) => l.filter((i) => i.id !== id)),
    substituir: (id, item) => setItens((l) => l.map((i) => (i.id === id ? { ...item, id } : i))),
    descartar, finalizar,
  };
}
